import { usePermission } from '@/lib/permissions/use-permission';
import type { PermissionStatus } from '@/lib/permissions/types';

export type AccessibilityPermissionStatus = 'unknown' | 'granted' | 'denied';

export interface AccessibilityPermissionFlow {
  /** Coarse permission state. `unknown` until the first probe resolves. */
  status: AccessibilityPermissionStatus;
  /** Boolean projection of `status` for consumers that compare against
   *  `null` semantics. `null` = unknown, `true` = granted, `false` = denied. */
  granted: boolean | null;
  /** Trigger the AX permission prompt (System Settings → Privacy &
   *  Security → Accessibility) and resolve to the next status. */
  request: () => Promise<AccessibilityPermissionStatus>;
  /** Re-run the permission probe on demand. */
  refresh: () => Promise<void>;
  /** Always false — the settle-window hack is deleted. The sidecar returns
   *  fresh uncached status on the next 1s poll, so no settle wait is needed. */
  settling: false;
  /** Always false — restart is now driven by the recording error layer
   *  (AudioCaptureError::RequiresRestart) surfaced through the recording
   *  controller, not by the permission status. */
  needsRestart: false;
}

/**
 * Single source of truth for macOS Accessibility (AX) permission state.
 *
 * Collapsed onto `usePermission("accessibility")` (react-query 1s poll) in
 * M5. The bespoke `presence_get_accessibility_permission_status` / 5s
 * setInterval / 15s SETTLE_WINDOW_MS are deleted — the sidecar gives
 * fresh uncached status on each 1s poll, so the settle-window hack is
 * no longer needed.
 *
 * The settle-window logic (`settling`, `needsRestart`) is preserved in the
 * interface (always-false) for backwards compatibility with callers that
 * read these fields (recordings-settings.tsx), but they are never true.
 */
export function useAccessibilityPermissionFlow(): AccessibilityPermissionFlow {
  const perm = usePermission('accessibility');

  const permStatus = perm.status;

  const status: AccessibilityPermissionStatus = mapStatus(permStatus);

  const granted: boolean | null =
    status === 'unknown' ? null : status === 'granted';

  const request = async (): Promise<AccessibilityPermissionStatus> => {
    try {
      const newStatus = await perm.request();
      return mapStatus(newStatus);
    } catch {
      return 'unknown';
    }
  };

  const refresh = async (): Promise<void> => {
    await perm.refetch();
  };

  return {
    status,
    granted,
    request,
    refresh,
    settling: false,
    needsRestart: false,
  };
}

function mapStatus(s: PermissionStatus | undefined): AccessibilityPermissionStatus {
  if (!s) return 'unknown';
  if (s === 'granted') return 'granted';
  return 'denied';
}
